import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { AlertTriangle, Database, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// PANNELLO TEMPORANEO — Migrazione listino C/D/E in Production.
// Visibile solo in SettingsPage (pagina gia' admin-only). Da rimuovere dopo l'esecuzione.
const MIGRATION_ID = 'PRICE_LIST_CDE_2026_PRODUCTION_01';
const EXECUTE_ENDPOINT = '/admin/migrations/price-list-cde/execute';
const DRYRUN_ENDPOINT = '/admin/migrations/price-list-cde/dry-run';

const MigrationCDEPanel = () => {
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [executed, setExecuted] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const loadPlan = useCallback(async () => {
    setPlanLoading(true);
    try {
      const res = await api.get(DRYRUN_ENDPOINT);
      const d = res.data || {};
      setPlan(d);
      const oc = d.operation_counts || {};
      const pending = (oc.UPDATE || 0) + (oc.INSERT || 0) + (oc.DEACTIVATE || 0);
      setExecuted(pending === 0);
    } catch (e) {
      toast.error('Impossibile leggere il piano di migrazione');
    } finally {
      setPlanLoading(false);
    }
  }, []);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const isSuccess = result && result.status === 'PRICE_LIST_CDE_2026_PRODUCTION_SUCCESS';
  const isAborted = result && typeof result.status === 'string' && result.status.startsWith('MIGRATION_ABORTED');

  const openConfirm = () => { setConfirmText(''); setResult(null); setConfirmOpen(true); };

  const runMigration = async () => {
    if (confirmText !== MIGRATION_ID) return;
    setRunning(true);
    setConfirmOpen(false);
    try {
      const res = await api.post(EXECUTE_ENDPOINT, { confirm: MIGRATION_ID, execute: true });
      setResult(res.data);
      if (res.data && res.data.status === 'PRICE_LIST_CDE_2026_PRODUCTION_SUCCESS') {
        setExecuted(true);
        toast.success('Migrazione COMPLETATA');
      } else {
        // MIGRATION_ABORTED_* o altro: mostra integralmente, nessun retry automatico
        toast.warning(`Esito: ${res.data?.status || 'sconosciuto'}`);
      }
    } catch (e) {
      // Errore HTTP: mostra integralmente la risposta del backend senza ritentare
      setResult(e.response?.data || { status: 'REQUEST_ERROR', message: String(e) });
      toast.error('Errore durante la chiamata — nessun retry automatico');
    } finally {
      setRunning(false);
    }
  };

  const oc = plan?.operation_counts || {};
  const env = plan?.environment || {};

  return (
    <Card className="border-2 border-amber-400 bg-amber-50/40" data-testid="cde-migration-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle data-testid="cde-migration-title">Migrazione listino C/D/E — Production</CardTitle>
              <CardDescription>Controllo temporaneo. Migration ID: {MIGRATION_ID}</CardDescription>
            </div>
          </div>
          {planLoading ? (
            <Badge variant="secondary" data-testid="cde-migration-status">Verifica…</Badge>
          ) : executed ? (
            <Badge className="bg-green-600 hover:bg-green-600" data-testid="cde-migration-status">COMPLETATA</Badge>
          ) : (
            <Badge className="bg-amber-600 hover:bg-amber-600" data-testid="cde-migration-status">NON ESEGUITA</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-100/60 rounded-md p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Esegue il write reale del solo catalogo <strong>price_list</strong> verso il target canonico
            C=176 / D=30 / E=52 (258). Il backend applica tutti gli hard-gate (DB/host Production, transaction
            reale, anti plan-drift, backup, invarianti storici, C0163/C0211, VAT, supplier, C16, idempotenza).
            Nessuna scrittura avviene se un gate fallisce.
          </span>
        </div>

        {!planLoading && plan && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cde-migration-plan">
            <PlanStat label="UPDATE" value={oc.UPDATE} />
            <PlanStat label="INSERT" value={oc.INSERT} />
            <PlanStat label="DEACTIVATE" value={oc.DEACTIVATE} />
            <PlanStat label="NO_CHANGE" value={oc.NO_CHANGE} />
            <PlanStat label="CONFLICT" value={oc.CONFLICT} />
            <PlanStat label="Stato piano" value={plan.plan_valid ? 'VALID' : 'INVALID'} />
            <PlanStat label="DB" value={env.db_name} small />
            <PlanStat label="Host" value={env.host_sanitized} small />
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            onClick={openConfirm}
            disabled={running || planLoading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="cde-migration-execute-btn"
          >
            {running ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Esecuzione…</>) : 'ESEGUI MIGRAZIONE PRODUCTION'}
          </Button>
          <Button variant="outline" onClick={loadPlan} disabled={running || planLoading} data-testid="cde-migration-refresh-btn">
            Ricarica piano
          </Button>
        </div>

        {result && (
          <div className="space-y-2" data-testid="cde-migration-result">
            <div className={`flex items-center gap-2 font-semibold ${isSuccess ? 'text-green-700' : isAborted ? 'text-red-700' : 'text-amber-800'}`}>
              {isSuccess ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              <span data-testid="cde-migration-result-status">{result.status}</span>
            </div>
            <pre
              className="text-xs bg-slate-900 text-slate-100 rounded-md p-3 overflow-auto max-h-[480px] whitespace-pre-wrap break-words"
              data-testid="cde-migration-result-json"
            >
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="cde-migration-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Conferma esecuzione in Production</DialogTitle>
            <DialogDescription>
              Per procedere, digita esattamente la stringa di conferma. L'operazione scrive sul DB Production
              reale dentro una transaction e non può essere annullata se non tramite rollback dal backup.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <code className="block text-xs bg-slate-100 rounded p-2 select-all">{MIGRATION_ID}</code>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Digita la stringa di conferma"
              data-testid="cde-migration-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cde-migration-cancel-btn">
              Annulla
            </Button>
            <Button
              onClick={runMigration}
              disabled={confirmText !== MIGRATION_ID}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="cde-migration-confirm-btn"
            >
              Conferma ed esegui
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const PlanStat = ({ label, value, small }) => (
  <div className="bg-white border rounded-md px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`font-semibold ${small ? 'text-xs break-all' : 'text-lg'} text-slate-800`}>
      {value === undefined || value === null ? '—' : String(value)}
    </div>
  </div>
);

export default MigrationCDEPanel;
